import React, { useState, useEffect } from 'react';
import { Redirect } from 'react-router-dom';
import ApprovalStatus from '../Modals/ApprovalStatus';
import { Modal } from '@mui/material';
import CasesAPI from 'api/CasesAPI';
import { useHistory } from 'react-router-dom';
import { useSnackbar } from 'notistack';
import { handleUpdateTicketNotification } from 'utils/NotificationsFuncs';
import { eccnTicketReportPDF } from 'components/Common/EntityTicketPdf';
import EmailAPI from 'api/EmailAPI';
import LicenseRes from 'components/TradeHelper/LicenseRes';
import ResultsDisplay from 'components/TradeHelper/ResultsDisplay';

interface IClassResultsProps {
  cur_user: any;
  cur_case: any;
  trigger: boolean;
  setTrigger: any;
  triggerCSV: boolean;
  setTriggerCSV: any;
  triggerSendPdf?: boolean;
  setTriggerSendPdf?: any;
  sendPdfFileName?: string;
  sendPdfEmailRecipients?: string[];
  onTariffPdfSendComplete?: (success: boolean) => void;
}

const ECCNResultsComponent: React.FC<IClassResultsProps> = ({
  cur_user,
  cur_case,
  trigger,
  setTrigger,
  triggerCSV,
  setTriggerCSV,
  triggerSendPdf = false,
  setTriggerSendPdf,
  sendPdfFileName,
  sendPdfEmailRecipients = [],
  onTariffPdfSendComplete,
}) => {
  const history = useHistory();

  if (!cur_case) {
    history.push('/bite-monitor');
    return <></>;
  }
  const [ isApprovalModalOpen, setIsApprovalModalOpen ] = useState(false);
  const [ , setResponses ] = useState<Record<string, string>>({});

  const { enqueueSnackbar } = useSnackbar();

  const preparedBy =
    [cur_user?.basics?.firstName, cur_user?.basics?.lastName]
      .filter(Boolean)
      .join(' ')
      .trim() ||
    cur_user?.basics?.email ||
    'BITE Export Controls';

  const buildEccnReportData = () => ({
    ticketName: cur_case.data.ticketName,
    docType: cur_case.data.docType,
    classificationValue: cur_case.data.classificationValue,
    commodity: {
      name: cur_case.data?.commodity?.commodityName,
      eccn: cur_case.data?.commodity?.eccn,
      destinationCountry: cur_case.data?.commodity?.destinationCountry,
    },
    eccnResults: Array.isArray(cur_case.data.eccnResults)
      ? cur_case.data.eccnResults
      : [],
    status: cur_case.data.status,
    frequency: cur_case.data.frequency,
    lastScreen: cur_case.data.lastUpdate || cur_case.data.createdAt,
    preparedBy: preparedBy,
  });

  if (!cur_user.selected_team) {
    return <Redirect to='/' />;
  }

  const handleApprovalCloseModal = () => {
    setIsApprovalModalOpen(false);
  };

  const changeStatus = async(newStatus: string) => {
    const actObj: any = {
      user: {
        kc_id: cur_user.basics?.id,
        email: cur_user.basics?.email,
        firstName: cur_user.basics?.firstName,
        lastName: cur_user.basics?.lastName,
      },
      action: `changed the status from ${cur_case.data.status} to ${newStatus}`,
      timeStamp: Date.now(),
    };
    const update: any = {
      _id: cur_case.data._id,
      status: newStatus,
      activity: [ ...cur_case.data.activity, actObj ],
    };
    const res = await CasesAPI.updateCase(update);
    if (res?.success) {
      if (
        cur_case.data.sharedWith.length > 0 &&
        cur_user.basics.Id === cur_case.data.createdBy
      ) {
        cur_case.data.sharedWith.forEach(async(user: string) => {
          handleUpdateTicketNotification(
            cur_case.data.ticketName,
            cur_user,
            user,
            cur_case.data._id,
          );
        });
      } else if (
        cur_case.data.sharedWith.length > 0 &&
        cur_user.basics.Id !== cur_case.data.createdBy
      ) {
        const shareArr = cur_case.data.sharedWith.filter((user: string) => {
          return user !== cur_user.basics.id && user !== cur_user.basics.email;
        });
        shareArr.push(cur_case.data.createdBy);
        shareArr.forEach(async(user: string) => {
          handleUpdateTicketNotification(
            cur_case.data.ticketName,
            cur_user,
            user,
            cur_case.data._id,
          );
        });
      }
    } else {
      enqueueSnackbar('There was an error updating the status', {
        variant: 'error',
      });
    }
    setTrigger(!trigger);
  };

  const handleCSV = async () => {
    try {
      await eccnTicketReportPDF(
        buildEccnReportData(),
        `${cur_case.data.ticketName}.pdf`,
      );
    } finally {
      setTriggerCSV(false);
    }
  };

  const blobToBase64 = async (blob: Blob): Promise<string> => {
    const buffer = await blob.arrayBuffer();
    let binary = '';
    const bytes = new Uint8Array(buffer);
    const chunkSize = 0x8000;

    for (let i = 0; i < bytes.length; i += chunkSize) {
      binary += String.fromCharCode(...bytes.subarray(i, i + chunkSize));
    }

    return btoa(binary);
  };

  const handleSendPdf = async () => {
    let sendSucceeded = false;

    try {
      const resolvedFileName = `${
        sendPdfFileName || cur_case.data.ticketName || 'result_ticket'
      }.pdf`;
      const pdfBlob = await eccnTicketReportPDF(
        buildEccnReportData(),
        resolvedFileName,
        { shouldDownload: false },
      );

      if (!pdfBlob || sendPdfEmailRecipients.length === 0) {
        throw new Error('Please add at least one recipient email');
      }

      const pdfBase64 = await blobToBase64(pdfBlob);
      const senderName = `${cur_user?.basics?.firstName || ''} ${
        cur_user?.basics?.lastName || ''
      }`.trim();
      const emailRes = await EmailAPI.sendTariffResultPdf({
        recipients: sendPdfEmailRecipients,
        fileName: resolvedFileName,
        pdfBase64: pdfBase64,
        docType: cur_case?.data?.docType,
        ticketId: cur_case?.data?._id,
        ticketName: cur_case?.data?.ticketName,
        senderName: senderName,
        senderEmail: cur_user?.basics?.email,
      });

      if (emailRes?.success) {
        sendSucceeded = true;
        enqueueSnackbar('PDF emailed successfully', { variant: 'success' });
      } else {
        throw new Error(emailRes?.result || 'Failed to send PDF email');
      }
    } catch (error: any) {
      enqueueSnackbar(
        `Failed to send result PDF: ${error?.message || 'Unknown error'}`,
        { variant: 'error' },
      );
    } finally {
      setTriggerSendPdf?.(false);
      onTariffPdfSendComplete?.(sendSucceeded);
    }
  };

  useEffect(() => {
    const initialResponses = cur_case.data.followUps.reduce(
      (acc: any, item: any) => {
        acc[item.question] = item.answer || '';
        return acc;
      },
      {} as Record<string, string>,
    );
    setResponses(initialResponses);
  }, []);

  useEffect(() => {
    if (triggerCSV) {
      handleCSV();
    }
  }, [ triggerCSV ]);

  useEffect(() => {
    if (triggerSendPdf) {
      handleSendPdf();
    }
  }, [triggerSendPdf]);

  // define preferred order
  const preferredOrder = [
    'Summary of Applicable Controls',
    'Control Check Outcome',
  ];

  return (
    <>
      <div
        className='results-card-frame'
        style={{ paddingTop: '12px', paddingBottom: '0px' }}
      >
        <div className='tariff-inputs'>
          <div className='classify-header-ticket'>{'ECCN Code'}</div>
          <div className='classify-cont' style={{ padding: '0px' }}>
            <div className='classify-cont-sec'>
              <div className='classify-cont-sec-div'>
                <input
                  placeholder='N/A'
                  className='classify-cont-sec-input'
                  type='text'
                  disabled
                  value={cur_case.data.commodity.eccn}
                />
              </div>
            </div>
          </div>
        </div>
        <div className='tariff-inputs'>
          <div className='classify-header-ticket'>
            {'Country of Destination'}
          </div>
          <div className='classify-cont' style={{ padding: '0px' }}>
            <div className='classify-cont-sec'>
              <div className='classify-cont-sec-div'>
                <input
                  placeholder='N/A'
                  className='classify-cont-sec-input'
                  type='text'
                  disabled
                  value={cur_case.data.commodity.destinationCountry}
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className='results-card-frame'>
        <div className='results-card-frame-comp'>
          <div className='results-card-frame-head'>Last Screen</div>
          <div className='results-card-frame-body'>N/A</div>
          <div className='results-card-frame-footer'></div>
        </div>
        <div className='results-card-frame-comp'>
          <div className='results-card-frame-head'>Screening Frequency</div>
          <div className='results-card-frame-body'>N/A</div>
          <div className='results-card-frame-footer'></div>
        </div>
        <div className='results-card-frame-comp'>
          <div className='results-card-frame-head'>Status</div>
          <div className='results-card-frame-body'>
            <svg
              xmlns='http://www.w3.org/2000/svg'
              width='9'
              height='8'
              viewBox='0 0 9 8'
              fill='none'
              style={{ marginRight: '5px' }}
            >
              <circle
                cx='4.5'
                cy='4'
                r='3'
                fill={
                  cur_case.data.status === 'Complete' ?
                    'var(--card-text)' :
                    cur_case.data.status === 'Pending' ?
                      '#FDB022' :
                      cur_case.data.status === 'New Results' ?
                        '#4895EF' :
                        cur_case.data.status === 'Approved' ?
                          '#47CD89' :
                          cur_case.data.status === 'Rejected' ?
                            '#F04438' :
                            '#B67DFF'
                }
              />
            </svg>
            {cur_case.data.status}
          </div>
          <div
            className='results-card-frame-footer'
            onClick={() => setIsApprovalModalOpen(true)}
          >
            Change
          </div>
        </div>
        <div className='results-card-frame-comp'>
          <div className='results-card-frame-head'>Type</div>
          <div className='results-card-frame-body-doc'>
            <div className='results-card-frame-body-texts'>
              {cur_case.data.docType}
            </div>
          </div>
          <div className='results-card-frame-footer' style={{ height: '18px' }}>
            {' '}
          </div>
        </div>
      </div>
      <div className='results-frame-col' style={{ gap: '24px', background: 'transparent', border: 'none' }}>
        <LicenseRes
          userHasSearch={true}
          hasLicense={Boolean(
            cur_case.data.eccnResults.find(
              (item: { section: string }) =>
                item.section === 'license_requirement_note',
            ),
          )}
          licenseMsg={
            cur_case.data.eccnResults.find(
              (item: { section: string }) =>
                item.section === 'license_requirement_note',
            ) || []
          }
        />

        <div
          style={{
            display: 'flex',
            width: '100%',
          }}
        >
          <div>
              <ResultsDisplay
                message={[ ...cur_case.data.eccnResults ].sort((a, b) => {
                const aIndex = preferredOrder.indexOf(a.result?.heading);
                const bIndex = preferredOrder.indexOf(b.result?.heading);

                // If both are in preferredOrder, compare their indexes
                if (aIndex !== -1 && bIndex !== -1) {
                  return aIndex - bIndex;
                }

                // If only one is in preferredOrder, that one comes first
                if (aIndex !== -1) {
                  return -1;
                }
                if (bIndex !== -1) {
                  return 1;
                }

                // Otherwise, keep their original order
                return 0;
                })}
                ticket={false}
              />
          </div>
        </div>
      </div>
      <Modal open={isApprovalModalOpen} onClose={handleApprovalCloseModal}>
        <ApprovalStatus
          handleClose={handleApprovalCloseModal}
          changeStatus={changeStatus}
          curStatus={cur_case.data.status}
        />
      </Modal>
    </>
  );
};

export default ECCNResultsComponent;
